import { WeddingInvitation, RSVPResponse, Guest, Expense, UserProfile as AppUserProfile, GiftRecord } from '../../types/invitation';
import { DBWeddingInvitation, DBRSVPResponse, DBGuest, DBUserProfile, DBExpense, DBGiftRecord, SeatingTable, SeatingGuestRef } from './types';

export function transformDBInvitationToApp(dbInvitation: DBWeddingInvitation): WeddingInvitation {
  return {
    id: dbInvitation.id,
    inviteCode: dbInvitation.invite_code,
    brideName: dbInvitation.bride_name,
    groomName: dbInvitation.groom_name,
    weddingDate: dbInvitation.wedding_date,
    weddingTime: dbInvitation.wedding_time,
    venue: dbInvitation.venue,
    address: dbInvitation.address,
    hasWelcomeParty: dbInvitation.has_welcome_party ?? false,
    hasWeddingCeremony: dbInvitation.has_wedding_ceremony,
    hasReception: dbInvitation.has_reception,
    showTransportInfo: dbInvitation.show_transport_info,
    showContactInfo: dbInvitation.show_contact_info,
    transportDetails: dbInvitation.transport_details,
    welcomePartyVenue: dbInvitation.welcome_party_venue,
    welcomePartyAddress: dbInvitation.welcome_party_address,
    welcomePartyTime: dbInvitation.welcome_party_time,
    ceremonyVenue: dbInvitation.ceremony_venue,
    ceremonyAddress: dbInvitation.ceremony_address,
    ceremonyTime: dbInvitation.ceremony_time,
    mapUrl: dbInvitation.map_url,
    receptionVenue: dbInvitation.reception_venue,
    receptionAddress: dbInvitation.reception_address,
    receptionTime: dbInvitation.reception_time,
    template: dbInvitation.template,
    coverImage: dbInvitation.cover_image,
    welcomeMessage: dbInvitation.welcome_message,
    primaryColor: dbInvitation.primary_color,
    contactPhone: dbInvitation.contact_phone,
    contactEmail: dbInvitation.contact_email,
    createdAt: new Date(dbInvitation.created_at),
    isActive: dbInvitation.is_active,
    acceptSubmissions: dbInvitation.accept_submissions,
    userId: dbInvitation.user_id,
    footerBranding: dbInvitation.footer_branding ?? undefined,
  };
}

export function transformDBRSVPToApp(dbRSVP: DBRSVPResponse): RSVPResponse {
  return {
    id: dbRSVP.id,
    invitationId: dbRSVP.invitation_id,
    inviteCode: dbRSVP.invite_code,
    guestName: dbRSVP.guest_name,
    guestEmail: dbRSVP.guest_email,
    guestSide: dbRSVP.guest_side || 'groom',
    rsvpData: {
      attending: dbRSVP.attending,
      guestCount: dbRSVP.guest_count,
      dietaryRestrictions: dbRSVP.dietary_restrictions || undefined,
      specialRequests: dbRSVP.special_requests || undefined,
      message: dbRSVP.message || undefined,
      contactPhone: dbRSVP.contact_phone || undefined,
      contactEmail: dbRSVP.contact_email || undefined,
      needsPhysicalInvitation: dbRSVP.needs_physical_invitation || false,
      postalAddress: dbRSVP.postal_address || undefined,
    },
    submittedAt: new Date(dbRSVP.submitted_at),
  };
}

export function transformDBGuestToApp(dbGuest: DBGuest): Guest {
  return {
    id: dbGuest.id,
    name: dbGuest.name,
    email: dbGuest.email,
    phone: dbGuest.phone,
    group: dbGuest.guest_group,
  };
}

export function transformDBUserProfileToApp(dbProfile: DBUserProfile): AppUserProfile {
  return {
    id: dbProfile.id,
    userId: dbProfile.user_id,
    displayName: dbProfile.display_name,
    phone: dbProfile.phone,
    weddingDate: dbProfile.wedding_date,
    partnerName: dbProfile.partner_name,
    createdAt: new Date(dbProfile.created_at),
    updatedAt: new Date(dbProfile.updated_at),
  };
}

export function transformDBExpenseToApp(dbExpense: DBExpense): Expense {
  return {
    id: dbExpense.id,
    userId: dbExpense.user_id,
    category: dbExpense.category,
    itemName: dbExpense.item_name,
    description: dbExpense.description,
    amount: dbExpense.amount,
    currency: dbExpense.currency,
    paymentStatus: dbExpense.payment_status as 'paid' | 'unpaid' | 'pending',
    dueDate: dbExpense.due_date,
    paidDate: dbExpense.paid_date,
    isRecurring: dbExpense.is_recurring,
    createdAt: new Date(dbExpense.created_at),
    updatedAt: new Date(dbExpense.updated_at),
  };
}

export function transformDBGiftRecordToApp(dbRecord: DBGiftRecord): GiftRecord {
  return {
    id: dbRecord.id,
    invitationId: dbRecord.invitation_id,
    rsvpId: dbRecord.rsvp_id || undefined,
    userId: dbRecord.user_id,
    guestName: dbRecord.guest_name,
    guestEmail: dbRecord.guest_email || undefined,
    guestSide: dbRecord.guest_side || 'groom',
    amount: Number(dbRecord.amount),
    currency: dbRecord.currency,
    giftType: dbRecord.gift_type,
    giftDescription: dbRecord.gift_description || undefined,
    message: dbRecord.message || undefined,
    recordedAt: new Date(dbRecord.recorded_at),
    recordedBy: dbRecord.recorded_by || undefined,
    createdAt: new Date(dbRecord.created_at),
    updatedAt: new Date(dbRecord.updated_at),
  };
}

export function normalizeSeatingTables(seatingData: unknown): SeatingTable[] {
  if (!seatingData) return [];

  let rawTables: unknown[];

  if (Array.isArray(seatingData)) {
    rawTables = seatingData;
  } else if (
    typeof seatingData === 'object' &&
    seatingData !== null &&
    'tables' in seatingData &&
    Array.isArray((seatingData as { tables: unknown }).tables)
  ) {
    rawTables = (seatingData as { tables: unknown[] }).tables;
  } else {
    return [];
  }

  return rawTables.map((table: unknown) => {
    const t = table as Record<string, unknown>;
    const guests = Array.isArray(t.guests)
      ? t.guests.map((guest: unknown): SeatingGuestRef => {
          if (typeof guest === 'string') {
            return { id: guest };
          }
          const g = guest as Record<string, unknown>;
          return {
            id: String(g.id || ''),
            name: g.name ? String(g.name) : undefined,
            guestCount: typeof g.guestCount === 'number' ? g.guestCount : undefined,
          };
        })
      : [];

    return {
      id: String(t.id || ''),
      name: String(t.name || ''),
      capacity: typeof t.capacity === 'number' ? t.capacity : 10,
      guests,
    };
  });
}
